import { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router';
import { Save, Plus, Trash2, Download, ExternalLink, Edit2, X, Check, Filter, Settings, Search, ArrowUpDown, ArrowUp, ArrowDown, CheckCircle2, UserPlus, ChevronDown, ChevronUp } from 'lucide-react';
import { updateTask, addTask, deleteTask } from '../store';
import { Task, TaskStatus, TaskType, Priority, User, Project } from '../types';
import { useApp } from '../providers/AppProvider';
import { canCreateProjectTasks, canDeleteProjectTask, canEditTaskParameters, getProjectTeamUsers, isExternalPartnerUser } from '../utils/projectAccess';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { ensureCostItemCode } from '../utils/costItems';
import { getOpenTasks, getBugsTasks, getTaskOrder, sortByDueDate, sortByPriority } from '../utils/taskFiltering';
import { getTerm } from '../utils/terminology';
import { toast } from 'sonner';
import { APP_FILTER_LABEL_CLASS, APP_NATIVE_FIELD_CLASS } from '../constants/formControls';
import { WIDGET_PANEL_CLASS } from '../constants/widgetTheme';
import { getPriorityColors, getStatusColors, getTaskTypeColors } from '../constants/colorScheme';

type ColumnConfig = {
  id: string;
  label: string;
  visible: boolean;
  width?: string;
};

const EXTERNAL_RESTRICTED_COLUMN_IDS = new Set([
  'estimatedDays',
  'manMonths',
  'actualHours',
  'costItems',
  'epic',
  'story',
  'category',
]);

const COMPACT_EDITOR_FIELD_CLASS =
  'w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring';

export default function SpreadsheetView() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const { state, loading, reload } = useApp();
  const { projects, tasks, users, epics } = useOrganizationData(state) as {
    projects: Project[];
    tasks: Task[];
    users: User[];
    epics: any[];
  };
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [editedTask, setEditedTask] = useState<Task | null>(null);
  
  // Filters - Initialize from URL params
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [filterStatus, setFilterStatus] = useState<TaskStatus | 'All' | 'Open'>((searchParams.get('status') as TaskStatus) || 'Open');
  const [filterType, setFilterType] = useState<TaskType | 'All'>((searchParams.get('type') as TaskType) || 'All');
  const [filterPriority, setFilterPriority] = useState<Priority | 'All'>((searchParams.get('priority') as Priority) || 'All');
  const [filterAssignee, setFilterAssignee] = useState<string>(searchParams.get('assignee') || 'All');
  const [filterEpic, setFilterEpic] = useState<string>(searchParams.get('epic') || 'All');
  const [filterCostItem, setFilterCostItem] = useState<string>(searchParams.get('costItem') || 'All');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Column configuration
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { id: 'number', label: '#', visible: true, width: 'w-12' },
    { id: 'id', label: 'ID', visible: true, width: 'w-28' },
    { id: 'title', label: 'Title', visible: true, width: 'min-w-[200px]' },
    { id: 'description', label: 'Description', visible: true, width: 'min-w-[250px]' },
    { id: 'type', label: 'Type', visible: true, width: 'w-28' },
    { id: 'status', label: 'Status', visible: true, width: 'w-32' },
    { id: 'priority', label: 'Priority', visible: true, width: 'w-28' },
    { id: 'order', label: 'Order', visible: false, width: 'w-20' },
    { id: 'estimatedDays', label: 'Est. Days', visible: true, width: 'w-24' },
    { id: 'manMonths', label: 'Man-Months', visible: true, width: 'w-28' },
    { id: 'actualHours', label: 'Actual Hours', visible: true, width: 'w-28' },
    { id: 'costItems', label: 'Cost Links', visible: true, width: 'min-w-[220px]' },
    { id: 'assignees', label: 'Assignees', visible: true, width: 'min-w-[150px]' },
    { id: 'epic', label: 'Epic', visible: true, width: 'w-32' },
    { id: 'story', label: 'Story', visible: false, width: 'w-32' },
    { id: 'category', label: 'Category', visible: false, width: 'w-32' },
    { id: 'actions', label: 'Actions', visible: true, width: 'w-44' },
  ]);

  const currentUser = state?.currentUser ?? null;
  const allProjects = projects ?? [];
  const allTasks = tasks ?? [];
  const allUsers = users ?? [];
  const allEpics = epics ?? [];

  const project = allProjects.find((p) => p.id === projectId);
  const projectTasks = allTasks.filter((t) => t.projectId === projectId);
  const projectCostItems = project?.costs || [];
  const projectUsers = project ? getProjectTeamUsers(project, allUsers, state?.teams || []) : [];
  const projectEpics = allEpics.filter((epic) => epic.projectId === projectId);
  const canCreateTasks = canCreateProjectTasks(currentUser, project || null);
  const canEditTaskParams = canEditTaskParameters(currentUser, project || null);
  const canDeleteTasks = canDeleteProjectTask(currentUser, project || null);
  const isExternalPartner = isExternalPartnerUser(currentUser, project || null);

  useEffect(() => {
    if (!isExternalPartner) {
      return;
    }

    setShowColumnConfig(false);
    setColumns((previous) =>
      previous.map((column) =>
        EXTERNAL_RESTRICTED_COLUMN_IDS.has(column.id)
          ? { ...column, visible: false }
          : column
      )
    );
  }, [isExternalPartner]);

  // Update column labels based on project terminology
  useEffect(() => {
    if (!project) return;

    setColumns((previous) =>
      previous.map((column) => {
        // Apply terminology-aware labels
        switch (column.id) {
          case 'epic':
            return { ...column, label: getTerm('largeGoal', project.projectType) };
          case 'story':
            return { ...column, label: getTerm('dailyAction', project.projectType) };
          case 'type':
            // Show errorIssue terminology for bug-type column
            return { ...column, label: 'Type (variants)' };
          case 'status':
            return { ...column, label: 'Status / State' };
          default:
            return column;
        }
      })
    );
  }, [project?.projectType]);

  if (loading || !state) {
    return <div className="p-8 text-center">Loading spreadsheet...</div>;
  }

  if (!currentUser) {
    return <div className="p-8 text-center">Loading user context...</div>;
  }

  const refreshState = async () => {
    const email = currentUser.email;
    if (email) {
      await reload(email);
    }
  };

  const handleStartEdit = (task: Task) => {
    if (!canEditTaskParams) {
      return;
    }

    setEditingTaskId(task.id);
    setEditedTask({ ...task });
  };

  const handleCancelEdit = () => {
    setEditingTaskId(null);
    setEditedTask(null);
  };

  const handleSaveEdit = async () => {
    if (!canEditTaskParams) {
      return;
    }

    if (editedTask) {
      const currentTaskId = editingTaskId || editedTask.id;
      const originalTask = projectTasks.find((t) => t.id === currentTaskId) || null;

      // Prepare sanitized updates
      const allowedAssigneeIds = new Set(projectUsers.map((user) => user.id));
      const allowedCostItemIds = new Set(projectCostItems.map((item) => item.id));
      const sanitizedTask: Task = {
        ...editedTask,
        assigneeIds: (editedTask.assigneeIds || []).filter((assigneeId) =>
          allowedAssigneeIds.has(assigneeId)
        ),
        costItemIds: (editedTask.costItemIds || []).filter((costItemId) =>
          allowedCostItemIds.has(costItemId) || (originalTask?.costItemIds || []).includes(costItemId)
        ),
      };

      await updateTask(currentTaskId, sanitizedTask);
      await refreshState();
      setEditingTaskId(null);
      setEditedTask(null);
    }
  };

  const handleFieldChange = (field: keyof Task, value: any) => {
    if (editedTask) {
      setEditedTask({ ...editedTask, [field]: value });
    }
  };

  const handleAddRow = async () => {
    if (!canCreateTasks) {
      toast.error('You do not have permission to create tasks.');
      return;
    }

    // Validate that we have required identifiers
    const orgId = project?.organizationId || state?.currentOrganization?.id || currentUser.organizationId;
    if (!orgId) {
      toast.error('Unable to determine organization. Please refresh and try again.');
      return;
    }

    if (!projectId) {
      toast.error('Project ID not found.');
      return;
    }

    try {
      const newTask: Task = {
        organizationId: orgId,
        projectId: projectId,
        title: 'New Task',
        description: '',
        type: 'Feature',
        status: 'Not Started',
        priority: 'Medium',
        assigneeIds: [],
        parentTaskIds: [],
        subtaskIds: [],
        estimatedDays: 0,
        timeLogs: [],
        standupNotes: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        customerVisible: false,
        attachments: [],
        verificationLinks: [],
        externalLinks: [],
        costItemIds: [],
        changeLogs: [],
      } as Task;
      await addTask(newTask);
      toast.success('Task created successfully');
      
      // Refresh state with proper email validation
      const email = currentUser.email;
      if (email) {
        await reload(email);
      }
    } catch (error: any) {
      console.error('Error creating task:', error);
      toast.error(error.message || 'Failed to create task');
    }
  };

  const handleDeleteRow = async (taskId: string) => {
    if (!canDeleteTasks) {
      toast.error('Task deletion is restricted for your role.');
      return;
    }

    if (confirm('Are you sure you want to delete this task?')) {
      await deleteTask(taskId);
      await refreshState();
    }
  };

  const handleMarkComplete = async (task: Task) => {
    if (!canEditTaskParams) {
      toast.error('You do not have permission to update tasks.');
      return;
    }
    const completedStatus: TaskStatus = 'Completed';
    await updateTask(task.id, { status: completedStatus, updatedAt: new Date().toISOString() });
    await refreshState();
    toast.success(`"${task.title}" marked as complete`);
  };

  const handleAssignToMe = async (task: Task) => {
    if (!currentUser) return;
    const alreadyAssigned = (task.assigneeIds || []).includes(currentUser.id);
    if (alreadyAssigned) {
      toast.info('You are already assigned to this task.');
      return;
    }
    await updateTask(task.id, {
      assigneeIds: [...(task.assigneeIds || []), currentUser.id],
      updatedAt: new Date().toISOString(),
    });
    await refreshState();
    toast.success('Assigned to you');
  };

  const calculateActualHours = (task: Task): number => {
    return task.timeLogs.reduce((sum, log) => sum + log.hours, 0);
  };

  const toggleColumnVisibility = (columnId: string) => {
    setColumns(prev => 
      prev.map(col => col.id === columnId ? { ...col, visible: !col.visible } : col)
    );
  };

  const exportToCSV = () => {
    const visibleColumns = columns.filter(c => c.visible);
    const headers = visibleColumns.map(c => c.label);
    
    const rows = filteredTasks.map((task) => {
      const row: any[] = [];
      visibleColumns.forEach(col => {
        switch(col.id) {
          case 'number': row.push(projectTasks.indexOf(task) + 1); break;
          case 'order': row.push(getTaskOrder(task) ?? ''); break;
          case 'id': row.push(task.id); break;
          case 'title': row.push(task.title); break;
          case 'description': row.push(task.description); break;
          case 'type': row.push(task.type); break;
          case 'status': row.push(task.status); break;
          case 'priority': row.push(task.priority); break;
          case 'estimatedDays': row.push(task.estimatedDays); break;
          case 'manMonths': row.push(task.estimatedManMonths || 0); break;
          case 'actualHours': row.push(calculateActualHours(task)); break;
          case 'costItems':
            row.push(
              (task.costItemIds || [])
                .map((costItemId) => {
                  const item = projectCostItems.find((candidate) => candidate.id === costItemId);
                  return item ? ensureCostItemCode(item) : costItemId;
                })
                .join(';')
            );
            break;
          case 'assignees': row.push((task.assigneeIds || []).join(';')); break;
          case 'epic': row.push(task.epicId || ''); break;
          case 'story': row.push(task.storyId || ''); break;
          case 'category': row.push(task.category || ''); break;
        }
      });
      return row;
    });

    const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project?.name || 'project'}-tasks.csv`;
    a.click();
  };

  // Apply filters
  const filteredTasks = projectTasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         task.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         task.id.toLowerCase().includes(searchQuery.toLowerCase());
    const CLOSED_STATUSES = new Set(['Completed', 'Done', 'Not Required']);
    const matchesStatus = filterStatus === 'All' || (filterStatus === 'Open' ? !CLOSED_STATUSES.has(task.status) : task.status === filterStatus);
    const matchesType = filterType === 'All' || task.type === filterType;
    const matchesPriority = filterPriority === 'All' || task.priority === filterPriority;
    const matchesAssignee = filterAssignee === 'All' || 
                           (filterAssignee === 'Unassigned' && task.assigneeIds.length === 0) ||
                           task.assigneeIds.includes(filterAssignee);
    const matchesEpic = filterEpic === 'All' || task.epicId === filterEpic;
    const matchesCostItem = filterCostItem === 'All' ||
                           (filterCostItem === 'Unlinked' && (!task.costItemIds || task.costItemIds.length === 0)) ||
                           (task.costItemIds && task.costItemIds.includes(filterCostItem));
    
    return matchesSearch && matchesStatus && matchesType && matchesPriority && matchesAssignee && matchesEpic && matchesCostItem;
  });

  // Apply smart filtering utilities - Bugs prioritized, then open tasks
  let smartFilteredTasks = filterStatus === 'Open' 
    ? getOpenTasks(filteredTasks)
    : filteredTasks;

  // Prioritize bugs at the top
  const bugsFirst = getBugsTasks(smartFilteredTasks);
  const nonBugs = smartFilteredTasks.filter(t => t.type !== 'Bug');
  const bugsAtTop = [...bugsFirst, ...nonBugs];

  // Sorting function
  const handleSort = (columnId: string) => {
    if (sortColumn === columnId) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(columnId);
      setSortDirection('asc');
    }
  };

  // Apply sorting to filtered tasks with smart defaults
  const sortedTasks = [...bugsAtTop].sort((a, b) => {
    if (!sortColumn) {
      // Default sort: explicit order first, then due date.
      const aOrder = getTaskOrder(a);
      const bOrder = getTaskOrder(b);

      if (aOrder !== null && bOrder !== null && aOrder !== bOrder) {
        return aOrder - bOrder;
      }

      if (aOrder !== null && bOrder === null) {
        return -1;
      }

      if (aOrder === null && bOrder !== null) {
        return 1;
      }

      return sortByDueDate([a, b])[0].id === a.id ? -1 : 1;
    }

    let aValue: any;
    let bValue: any;

    switch (sortColumn) {
      case 'order':
        aValue = getTaskOrder(a) ?? Number.MAX_SAFE_INTEGER;
        bValue = getTaskOrder(b) ?? Number.MAX_SAFE_INTEGER;
        break;
      case 'title':
        aValue = a.title.toLowerCase();
        bValue = b.title.toLowerCase();
        break;
      case 'type':
        aValue = a.type;
        bValue = b.type;
        break;
      case 'status':
        aValue = a.status;
        bValue = b.status;
        break;
      case 'priority':
        // Custom priority ordering
        const priorityOrder: Record<Priority, number> = {
          'P0': 0, 'Critical': 1, 'P1': 2, 'High': 3, 'P2': 4, 'Medium': 5, 'Low': 6
        };
        aValue = priorityOrder[a.priority];
        bValue = priorityOrder[b.priority];
        break;
      case 'estimatedDays':
        aValue = a.estimatedDays;
        bValue = b.estimatedDays;
        break;
      case 'manMonths':
        aValue = a.estimatedManMonths || 0;
        bValue = b.estimatedManMonths || 0;
        break;
      case 'actualHours':
        aValue = calculateActualHours(a);
        bValue = calculateActualHours(b);
        break;
      case 'costItems':
        aValue = (a.costItemIds || []).length;
        bValue = (b.costItemIds || []).length;
        break;
      case 'id':
        aValue = a.id;
        bValue = b.id;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Render sort icon for column header
  const renderSortIcon = (columnId: string) => {
    if (sortColumn !== columnId) {
      return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3" />
      : <ArrowDown className="w-3 h-3" />;
  };

  const renderTaskActions = (task: Task, isEditing: boolean) => {
    const isComplete = ['Completed', 'Done'].includes(task.status);
    const isAssignedToMe = currentUser ? (task.assigneeIds || []).includes(currentUser.id) : false;
    return (
      <div className="flex items-center gap-1 flex-wrap">
        {!canEditTaskParams ? (
          <span className="text-xs text-muted-foreground">View only</span>
        ) : isEditing ? (
          <>
            <button
              onClick={handleSaveEdit}
              className="rounded p-1.5 text-primary transition-colors hover:bg-primary/10"
              title="Save changes"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={handleCancelEdit}
              className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted"
              title="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <>
            {/* Mark Complete — first in column */}
            {!isComplete && (
              <button
                onClick={() => handleMarkComplete(task)}
                className="rounded p-1.5 text-emerald-600 transition-colors hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/30"
                title="Mark as Complete"
              >
                <CheckCircle2 className="w-4 h-4" />
              </button>
            )}
            {/* Assign to me */}
            {!isAssignedToMe && (
              <button
                onClick={() => handleAssignToMe(task)}
                className="rounded p-1.5 text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/30"
                title="Assign to me"
              >
                <UserPlus className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => handleStartEdit(task)}
              className="rounded p-1.5 text-primary transition-colors hover:bg-primary/10"
              title="Edit task"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            {canDeleteTasks ? (
              <button
                onClick={() => handleDeleteRow(task.id)}
                className="rounded p-1.5 text-destructive transition-colors hover:bg-destructive/10"
                title="Delete task"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            ) : null}
          </>
        )}
      </div>
    );
  };

  const getEpicName = (epicId?: string) => {
    if (!epicId) return '-';
    const epic = projectEpics.find(e => e.id === epicId);
    return epic?.title || epicId;
  };

  const getUserName = (userId: string) => {
    const user = projectUsers.find(u => u.id === userId);
    return user?.name || userId;
  };

  const getCostItemDisplay = (costItemId: string): string => {
    const index = projectCostItems.findIndex((item) => item.id === costItemId);
    if (index >= 0) {
      const item = projectCostItems[index];
      const code = ensureCostItemCode(item, index);
      return `${code} - ${item.description}`;
    }

    const fallbackItem = projectCostItems.find((item) => ensureCostItemCode(item).toLowerCase() === costItemId.toLowerCase());
    if (fallbackItem) {
      const code = ensureCostItemCode(fallbackItem);
      return `${code} - ${fallbackItem.description}`;
    }

    return costItemId;
  };

  if (!project) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <p className="mb-4 text-muted-foreground">Project not found</p>
          <Link to="/app/projects" className="text-primary hover:text-primary/80">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4 pb-6 max-w-full mx-auto">
      {/* Header */}
      <div className="mb-5">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div>
            <h1 className="mb-1 text-2xl font-semibold text-foreground">Task Spreadsheet</h1>
            <p className="text-sm text-muted-foreground">{project.name}</p>
          </div>
          <div className="flex items-center gap-3">
            {!isExternalPartner && (
              <button
                onClick={() => setShowColumnConfig(!showColumnConfig)}
                className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-foreground hover:bg-muted"
              >
                <Settings className="w-4 h-4" />
                Columns
              </button>
            )}
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-foreground hover:bg-muted"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            {canCreateTasks && (
              <button
                onClick={handleAddRow}
                className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="w-4 h-4" />
                Add Task
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Column Configuration Panel */}
      {showColumnConfig && !isExternalPartner && (
        <div className={`${WIDGET_PANEL_CLASS} mb-6 p-4`}>
          <h3 className="mb-3 font-semibold text-foreground">Configure Columns</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {columns.map(col => (
              <label key={col.id} className="flex cursor-pointer items-center gap-2 text-foreground">
                <input
                  type="checkbox"
                  checked={col.visible}
                  onChange={() => toggleColumnVisibility(col.id)}
                  className="rounded"
                />
                <span className="text-sm">{col.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className={`${WIDGET_PANEL_CLASS} mb-6 p-4`}>
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-muted-foreground" />
            <h3 className="font-semibold text-foreground">Filters</h3>
          </div>
          <button
            type="button"
            onClick={() => setMobileFiltersOpen((previous) => !previous)}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-foreground hover:bg-muted md:hidden"
            title="Toggle filters"
            aria-label="Toggle filters"
          >
            {mobileFiltersOpen ? 'Hide' : 'Show'}
            {mobileFiltersOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        </div>
        <div className={`${mobileFiltersOpen ? 'block' : 'hidden'} md:block`}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          {/* Search */}
          <div className="space-y-1.5 sm:col-span-2 lg:col-span-2 xl:col-span-2">
            <span className={APP_FILTER_LABEL_CLASS}>Search</span>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full text-sm ${APP_NATIVE_FIELD_CLASS} pl-10 pr-4`}
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="space-y-1.5">
            <span className={APP_FILTER_LABEL_CLASS}>Status</span>
            <select
              title="Filter by task status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as TaskStatus | 'All' | 'Open')}
              className={`w-full text-sm ${APP_NATIVE_FIELD_CLASS}`}
            >
              <option value="All">All Status</option>
              <option value="Open">Open Tasks</option>
              <option value="Not Started">Not Started</option>
              <option value="Active">Active</option>
              <option value="In Progress">In Progress</option>
              <option value="In Review">In Review</option>
              <option value="Blocked">Blocked</option>
              <option value="Deferred">Deferred</option>
              <option value="Completed">Completed</option>
              <option value="Done">Done</option>
              <option value="Not Required">Not Required</option>
            </select>
          </div>

          {/* Type Filter */}
          <div className="space-y-1.5">
            <span className={APP_FILTER_LABEL_CLASS}>Type</span>
            <select
              title="Filter by task type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as TaskType | 'All')}
              className={`w-full text-sm ${APP_NATIVE_FIELD_CLASS}`}
            >
              <option value="All">All Types</option>
              <option value="Feature">Feature</option>
              <option value="Bug">Bug</option>
              <option value="Improvement">Improvement</option>
            </select>
          </div>

          {/* Priority Filter */}
          <div className="space-y-1.5">
            <span className={APP_FILTER_LABEL_CLASS}>Priority</span>
            <select
              title="Filter by task priority"
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value as Priority | 'All')}
              className={`w-full text-sm ${APP_NATIVE_FIELD_CLASS}`}
            >
              <option value="All">All Priorities</option>
              <option value="Critical">Critical</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
              <option value="P0">P0</option>
              <option value="P1">P1</option>
              <option value="P2">P2</option>
            </select>
          </div>

          {/* Assignee Filter */}
          <div className="space-y-1.5">
            <span className={APP_FILTER_LABEL_CLASS}>Assignee</span>
            <select
              title="Filter by assignee"
              value={filterAssignee}
              onChange={(e) => setFilterAssignee(e.target.value)}
              className={`w-full text-sm ${APP_NATIVE_FIELD_CLASS}`}
            >
              <option value="All">All Assignees</option>
              <option value="Unassigned">Unassigned</option>
              {projectUsers.map(user => (
                <option key={user.id} value={user.id}>{user.name}</option>
              ))}
            </select>
          </div>

          {!isExternalPartner && (
            <div className="space-y-1.5">
              <span className={APP_FILTER_LABEL_CLASS}>Epic</span>
              <select
                title="Filter by epic"
                value={filterEpic}
                onChange={(e) => setFilterEpic(e.target.value)}
                className={`w-full text-sm ${APP_NATIVE_FIELD_CLASS}`}
              >
                <option value="All">All Epics</option>
                {projectEpics.map(epic => (
                  <option key={epic.id} value={epic.id}>{epic.title}</option>
                ))}
              </select>
            </div>
          )}

          {!isExternalPartner && projectCostItems.length > 0 && (
            <div className="space-y-1.5">
              <span className={APP_FILTER_LABEL_CLASS}>Cost Items</span>
              <select
                title="Filter by cost item links"
                value={filterCostItem}
                onChange={(e) => setFilterCostItem(e.target.value)}
                className={`w-full text-sm ${APP_NATIVE_FIELD_CLASS}`}
              >
                <option value="All">All Cost Items</option>
                <option value="Unlinked">Unlinked Tasks</option>
                {projectCostItems.map(costItem => (
                  <option key={costItem.id} value={costItem.id}>
                    {ensureCostItemCode(costItem)} - {costItem.description.split(' - ')[0]}
                  </option>
                ))}
              </select>
            </div>
          )}
          </div>
        
          {/* Active Filters Summary */}
          {(searchQuery || filterStatus !== 'All' || filterType !== 'All' || filterPriority !== 'All' || filterAssignee !== 'All' || filterEpic !== 'All' || filterCostItem !== 'All') && (
            <div className="mt-4 text-sm text-muted-foreground">
              Showing {filteredTasks.length} of {projectTasks.length} tasks
            </div>
          )}
        </div>
      </div>

      {/* Spreadsheet */}
      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-border bg-muted/40">
              <tr>
                {columns.filter(c => c.visible).map(col => (
                  <th key={col.id} className={`px-3 py-3 text-left font-medium text-muted-foreground ${col.width}`}>
                    {col.id === 'actions' ? (
                      col.label
                    ) : (
                      <div className="flex items-center gap-1 cursor-pointer" onClick={() => handleSort(col.id)}>
                        {col.label}
                        {renderSortIcon(col.id)}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sortedTasks.length === 0 ? (
                <tr>
                  <td colSpan={columns.filter(c => c.visible).length + 1} className="px-4 py-12 text-center text-muted-foreground">
                    {searchQuery || filterStatus !== 'All' || filterType !== 'All' || filterPriority !== 'All' || filterAssignee !== 'All' || filterEpic !== 'All'
                      ? 'No tasks match your filters'
                      : 'No tasks yet. Click "Add Task" to create one.'}
                  </td>
                </tr>
              ) : (
                sortedTasks.map((task, index) => {
                  const isEditing = canEditTaskParams && editingTaskId === task.id;
                  const displayTask = isEditing && editedTask ? editedTask : task;
                  const taskOrder = getTaskOrder(task);

                  return (
                    <tr key={task.id} className={`hover:bg-muted/40 ${isEditing ? 'bg-primary/10' : ''}`}>
                      {/* Number */}
                      {columns.find(c => c.id === 'number')?.visible && (
                        <td className="px-3 py-3 text-muted-foreground">{index + 1}</td>
                      )}

                      {/* ID Column - clickable link */}
                      {columns.find(c => c.id === 'id')?.visible && (
                        <td className="px-3 py-3">
                          {isEditing ? (
                            <input
                              type="text"
                              title="ID Label (for display/routing)"
                              value={displayTask.idLabel || ''}
                              onChange={(e) => handleFieldChange('idLabel' as any, e.target.value || undefined)}
                              className={COMPACT_EDITOR_FIELD_CLASS}
                              placeholder="e.g., FEAT-123"
                            />
                          ) : (
                            <Link
                              to={`/task/${task.id}`}
                              className="inline-flex items-center gap-1 text-primary hover:underline font-medium"
                              title="View task details"
                            >
                              <code className="rounded bg-muted px-2 py-1 text-xs truncate">
                                {task.idLabel || task.id?.slice(-3).toUpperCase() || '---'}
                              </code>
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />
                            </Link>
                          )}
                        </td>
                      )}

                      {/* Order (shown if user enables it) */}
                      {columns.find(c => c.id === 'order')?.visible && (
                        <td className="px-3 py-3 text-foreground">{taskOrder ?? '-'}</td>
                      )}

                      {/* Title */}
                      {columns.find(c => c.id === 'title')?.visible && (
                        <td className="px-3 py-3">
                          {isEditing ? (
                            <input
                              type="text"
                              title="Task title"
                              value={displayTask.title}
                              onChange={(e) => handleFieldChange('title', e.target.value)}
                              className={COMPACT_EDITOR_FIELD_CLASS}
                            />
                          ) : (
                            <Link
                              to={`/task/${task.id}`}
                              className="inline-flex items-center gap-1 font-medium text-foreground hover:text-primary hover:underline"
                              title="Open task details"
                            >
                              <span className="truncate">{task.title}</span>
                              <ExternalLink className="h-3 w-3 flex-shrink-0" />
                            </Link>
                          )}
                        </td>
                      )}

                      {/* Description */}
                      {columns.find(c => c.id === 'description')?.visible && (
                        <td className="px-3 py-3">
                          {isEditing ? (
                            <textarea
                              title="Task description"
                              value={displayTask.description}
                              onChange={(e) => handleFieldChange('description', e.target.value)}
                              className={`${COMPACT_EDITOR_FIELD_CLASS} min-h-[3rem]`}
                              rows={2}
                            />
                          ) : (
                            <span className="line-clamp-2 text-muted-foreground">{task.description}</span>
                          )}
                        </td>
                      )}

                      {/* Type */}
                      {columns.find(c => c.id === 'type')?.visible && (
                        <td className="px-3 py-3">
                          {isEditing ? (
                            <select
                              title="Task type"
                              value={displayTask.type}
                              onChange={(e) => handleFieldChange('type', e.target.value)}
                              className={COMPACT_EDITOR_FIELD_CLASS}
                            >
                              <option value="Feature">Feature</option>
                              <option value="Bug">Bug</option>
                              <option value="Improvement">Improvement</option>
                            </select>
                          ) : (
                            <span className={`inline-flex rounded border px-2 py-1 text-xs ${getTaskTypeColors(task.type).badge}`}>
                              {task.type}
                            </span>
                          )}
                        </td>
                      )}

                      {/* Status */}
                      {columns.find(c => c.id === 'status')?.visible && (
                        <td className="px-3 py-3">
                          {isEditing ? (
                            <select
                              title="Task status"
                              value={displayTask.status}
                              onChange={(e) => handleFieldChange('status', e.target.value)}
                              className={COMPACT_EDITOR_FIELD_CLASS}
                            >
                              <option value="Not Started">Not Started</option>
                              <option value="Active">Active</option>
                              <option value="In Progress">In Progress</option>
                              <option value="Completed">Completed</option>
                              <option value="Blocked">Blocked</option>
                              <option value="Deferred">Deferred</option>
                            </select>
                          ) : (
                            <span className={`inline-flex whitespace-nowrap rounded border px-2 py-1 text-xs ${getStatusColors(task.status).badge}`}>
                              {task.status}
                            </span>
                          )}
                        </td>
                      )}

                      {/* Priority */}
                      {columns.find(c => c.id === 'priority')?.visible && (
                        <td className="px-3 py-3">
                          {isEditing ? (
                            <select
                              title="Task priority"
                              value={displayTask.priority}
                              onChange={(e) => handleFieldChange('priority', e.target.value)}
                              className={COMPACT_EDITOR_FIELD_CLASS}
                            >
                              <option value="Critical">Critical</option>
                              <option value="High">High</option>
                              <option value="Medium">Medium</option>
                              <option value="Low">Low</option>
                              <option value="P0">P0</option>
                              <option value="P1">P1</option>
                              <option value="P2">P2</option>
                            </select>
                          ) : (
                            <span className={`inline-flex rounded border px-2 py-1 text-xs ${getPriorityColors(task.priority).badge}`}>
                              {task.priority}
                            </span>
                          )}
                        </td>
                      )}

                      {/* Estimated Days */}
                      {columns.find(c => c.id === 'estimatedDays')?.visible && (
                        <td className="px-3 py-3">
                          {isEditing ? (
                            <input
                              type="number"
                              title="Estimated days"
                              value={displayTask.estimatedDays}
                              onChange={(e) => handleFieldChange('estimatedDays', parseFloat(e.target.value) || 0)}
                              className={COMPACT_EDITOR_FIELD_CLASS}
                              min="0"
                              step="0.5"
                            />
                          ) : (
                            <span className="text-muted-foreground">{task.estimatedDays}</span>
                          )}
                        </td>
                      )}

                      {/* Man-Months */}
                      {columns.find(c => c.id === 'manMonths')?.visible && (
                        <td className="px-3 py-3">
                          {isEditing ? (
                            <input
                              type="number"
                              title="Estimated man-months"
                              value={displayTask.estimatedManMonths || 0}
                              onChange={(e) => handleFieldChange('estimatedManMonths', parseFloat(e.target.value) || 0)}
                              className={COMPACT_EDITOR_FIELD_CLASS}
                              min="0"
                              step="0.5"
                            />
                          ) : (
                            <span className="text-muted-foreground">{task.estimatedManMonths || '-'}</span>
                          )}
                        </td>
                      )}

                      {/* Actual Hours */}
                      {columns.find(c => c.id === 'actualHours')?.visible && (
                        <td className="px-3 py-3">
                          <span className="text-muted-foreground">{calculateActualHours(task)}</span>
                        </td>
                      )}

                      {/* Cost Links */}
                      {columns.find(c => c.id === 'costItems')?.visible && (
                        <td className="px-3 py-3">
                          {isEditing ? (
                            projectCostItems.length === 0 ? (
                              <span className="text-xs text-muted-foreground">No project cost items</span>
                            ) : (
                              <select
                                multiple
                                title="Linked cost items"
                                value={displayTask.costItemIds || []}
                                onChange={(e) => {
                                  const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                                  handleFieldChange('costItemIds', selected);
                                }}
                                className={COMPACT_EDITOR_FIELD_CLASS}
                                size={Math.min(4, Math.max(3, projectCostItems.length))}
                              >
                                {projectCostItems.map((item, itemIndex) => (
                                  <option key={item.id} value={item.id}>
                                    {ensureCostItemCode(item, itemIndex)} - {item.description}
                                  </option>
                                ))}
                              </select>
                            )
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {(task.costItemIds || []).length > 0 ? (
                                (task.costItemIds || []).map((costItemId) => (
                                  <span
                                    key={costItemId}
                                    className={`inline-flex rounded border px-2 py-1 text-xs ${getStatusColors('Deferred').badge}`}
                                    title={getCostItemDisplay(costItemId)}
                                  >
                                    {getCostItemDisplay(costItemId)}
                                  </span>
                                ))
                              ) : (
                                <span className="text-xs text-muted-foreground">No linked costs</span>
                              )}
                            </div>
                          )}
                        </td>
                      )}

                      {/* Assignees */}
                      {columns.find(c => c.id === 'assignees')?.visible && (
                        <td className="px-3 py-3">
                          {isEditing ? (
                            <select
                              multiple
                              title="Task assignees"
                              value={displayTask.assigneeIds}
                              onChange={(e) => {
                                const selected = Array.from(e.target.selectedOptions, option => option.value);
                                handleFieldChange('assigneeIds', selected);
                              }}
                              className={COMPACT_EDITOR_FIELD_CLASS}
                              size={3}
                            >
                              {projectUsers.map(user => (
                                <option key={user.id} value={user.id}>
                                  {user.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {task.assigneeIds.length > 0 ? (
                                task.assigneeIds.map(userId => (
                                  <span key={userId} className={`inline-flex rounded border px-2 py-1 text-xs ${getStatusColors('Active').badge}`}>
                                    {getUserName(userId)}
                                  </span>
                                ))
                              ) : (
                                <span className="text-xs text-muted-foreground">Unassigned</span>
                              )}
                            </div>
                          )}
                        </td>
                      )}

                      {/* Epic */}
                      {columns.find(c => c.id === 'epic')?.visible && (
                        <td className="px-3 py-3">
                          {isEditing ? (
                            <select
                              title="Task epic"
                              value={displayTask.epicId || ''}
                              onChange={(e) => handleFieldChange('epicId', e.target.value || undefined)}
                              className={COMPACT_EDITOR_FIELD_CLASS}
                            >
                              <option value="">None</option>
                              {projectEpics.map(epic => (
                                <option key={epic.id} value={epic.id}>{epic.title}</option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-xs text-muted-foreground">{getEpicName(task.epicId)}</span>
                          )}
                        </td>
                      )}

                      {/* Story */}
                      {columns.find(c => c.id === 'story')?.visible && (
                        <td className="px-3 py-3">
                          <span className="text-xs text-muted-foreground">{task.storyId || '-'}</span>
                        </td>
                      )}

                      {/* Category */}
                      {columns.find(c => c.id === 'category')?.visible && (
                        <td className="px-3 py-3">
                          {isEditing ? (
                            <input
                              type="text"
                              title="Task category"
                              value={displayTask.category || ''}
                              onChange={(e) => handleFieldChange('category', e.target.value)}
                              className={COMPACT_EDITOR_FIELD_CLASS}
                            />
                          ) : (
                            <span className="text-xs text-muted-foreground">{task.category || '-'}</span>
                          )}
                        </td>
                      )}

                      {/* Actions Column */}
                      {columns.find(c => c.id === 'actions')?.visible && (
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            {!canEditTaskParams ? (
                              <span className="text-xs text-muted-foreground">View only</span>
                            ) : isEditing ? (
                              <>
                                <button
                                  onClick={handleSaveEdit}
                                  className="rounded p-2 text-primary transition-colors hover:bg-primary/10"
                                  title="Save changes"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="rounded p-2 text-muted-foreground transition-colors hover:bg-muted"
                                  title="Cancel"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleStartEdit(task)}
                                  className="rounded p-2 text-primary transition-colors hover:bg-primary/10"
                                  title="Edit task"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                {canDeleteTasks && task.id ? (
                                  <button
                                    onClick={() => handleDeleteRow(task.id!)}
                                    className="rounded p-2 text-destructive transition-colors hover:bg-destructive/10"
                                    title="Delete task"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                ) : null}
                              </>
                            )}
                          </div>
                        </td>
                      )}

                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}